import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaPalette } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import './ReminderCategories.css';

function ReminderCategories() {
  const [categories, setCategories] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#4CAF50',
    icon: '📁'
  });
  const [loading, setLoading] = useState(true);

  const defaultIcons = ['📁', '📌', '🏠', '💼', '🎯', '⭐', '🔔', '📝', '💡', '🎉', '🚀', '❤️'];
  const defaultColors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#FFEB3B', '#795548'];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/reminders/categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Chyba pri načítaní kategórií');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/reminders/categories/${editingId}`, formData);
      } else {
        await api.post('/reminders/categories', formData);
      }
      toast.success(editingId ? 'Kategória aktualizovaná' : 'Kategória vytvorená');
      setFormData({ name: '', color: '#4CAF50', icon: '📁' });
      setEditingId(null);
      setShowAddForm(false);
      fetchCategories();
    } catch (error) {
      toast.error('Chyba pri ukladaní kategórie');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Naozaj chcete zmazať túto kategóriu? Pripomienky v tejto kategórii nebudú zmazané.')) return;

    try {
      await api.delete(`/reminders/categories/${id}`);
      toast.success('Kategória zmazaná');
      fetchCategories();
    } catch (error) {
      toast.error('Chyba pri mazaní kategórie');
    }
  };

  const startEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      name: category.name,
      color: category.color,
      icon: category.icon
    });
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ name: '', color: '#4CAF50', icon: '📁' });
    setShowAddForm(false);
  };

  return (
    <div className="reminder-categories-container">
      <div className="page-header">
        <h1>Kategórie pripomienok</h1>
        <motion.button 
          className="add-btn"
          onClick={() => setShowAddForm(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaPlus /> Nová kategória
        </motion.button>
      </div>

      <AnimatePresence>
        {showAddForm && (
          <motion.div 
            className="category-form-container"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <form onSubmit={handleSubmit} className="category-form">
              <h2>{editingId ? 'Upraviť kategóriu' : 'Nová kategória'}</h2>
              
              <div className="form-row">
                <div className="input-group">
                  <label>Názov kategórie</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Napr. Práca, Domácnosť, Osobné..."
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label>Ikona</label>
                  <div className="icon-selector">
                    {defaultIcons.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        className={`icon-option ${formData.icon === icon ? 'selected' : ''}`}
                        onClick={() => setFormData({...formData, icon})}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label>Farba</label>
                  <div className="color-selector">
                    {defaultColors.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`color-option ${formData.color === color ? 'selected' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({...formData, color})}
                      />
                    ))}
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({...formData, color: e.target.value})}
                      className="color-picker"
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="save-btn">
                  <FaSave /> {editingId ? 'Aktualizovať' : 'Vytvoriť'}
                </button>
                <button type="button" onClick={cancelEdit} className="cancel-btn">
                  <FaTimes /> Zrušiť
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="categories-grid">
        {loading ? (
          <div className="loading">Načítavam kategórie...</div>
        ) : categories.length === 0 ? (
          <div className="no-data">
            <p>Zatiaľ nemáte žiadne kategórie</p>
            <button onClick={() => setShowAddForm(true)}>Vytvoriť prvú kategóriu</button>
          </div>
        ) : (
          categories.map((category, index) => (
            <motion.div
              key={category.id}
              className="category-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              style={{ borderLeftColor: category.color }}
            >
              <div className="category-header">
                <div className="category-icon" style={{ color: category.color }}>
                  {category.icon}
                </div>
                <h3>{category.name}</h3>
              </div>
              
              <div className="category-meta">
                <span className="reminder-count">
                  {category.reminder_count} {category.reminder_count === 1 ? 'pripomienka' : 
                    category.reminder_count < 5 ? 'pripomienky' : 'pripomienok'}
                </span>
              </div>

              <div className="category-actions">
                <motion.button
                  className="edit-btn"
                  onClick={() => startEdit(category)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Upraviť"
                >
                  <FaEdit />
                </motion.button>
                <motion.button
                  className="delete-btn"
                  onClick={() => deleteCategory(category.id)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  title="Zmazať"
                >
                  <FaTrash />
                </motion.button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}

export default ReminderCategories;